import { useAppState } from "contexts/UserContext";
import { useCause } from "contexts/CauseContext";
import { usePeriodManagement } from "./usePeriodManagement";
import { usePeriodMoodFiltering } from "./usePeriodMoodFiltering";
import { useMoodAggregation } from "../../../shared/charts/moodBalance/useMoodAggregation";
import { usePieChartData } from "../../../shared/charts/moodBalance/usePieChartData";
import { useCauseAnalysis } from "./useCauseAnalysis";
import useCauseProcessor from "../../../hooks/useCauseProcessor";
import type { Mood } from "../../../types/models";

export interface BalancePieDataItem {
  name: string;
  value: number;
  percentage: string;
  label: string;
  color?: string;
}

export interface UseTrendsDataReturn {
  availablePeriods: string[];
  selectedPeriod: string;
  onPeriodChange: (period: string) => void;
  filteredMoods: Mood[];
  moodCounts: Record<string, number>;
  totalMoods: number;
  balancePieData: BalancePieDataItem[];
  filteredCauses: Record<string, number>;
}

export function useTrendsData(): UseTrendsDataReturn {
  const { moods } = useAppState();
  const { causes, causeTypesTranslations } = useCause();
  const { allCauseTypes } = useCauseProcessor();

  const periodManagement = usePeriodManagement(moods || []);

  const filteredMoods = usePeriodMoodFiltering(
    (moods || []) as Mood[],
    periodManagement.selectedPeriod
  );

  const { moodCounts, totalMoods } = useMoodAggregation(filteredMoods);

  const { balancePieData } = usePieChartData(moodCounts, totalMoods);

  const { filteredCauses } = useCauseAnalysis(
    filteredMoods,
    causes || [],
    allCauseTypes,
    causeTypesTranslations
  );

  return {
    availablePeriods: periodManagement.availablePeriods,
    selectedPeriod: periodManagement.selectedPeriod,
    onPeriodChange: periodManagement.onPeriodChange,
    filteredMoods,
    moodCounts,
    totalMoods,
    balancePieData,
    filteredCauses,
  };
}
